import { cn } from "@web/lib/cn";

const PALETTE_SIZE = 6;
const GENERIC_LOCAL_PARTS = new Set(["me", "mail", "info", "hello", "contact", "hi"]);

export function accountColor(index: number): string {
  const i = ((index % PALETTE_SIZE) + PALETTE_SIZE) % PALETTE_SIZE;
  return `var(--acct-${i})`;
}

function domainWord(domain: string): string {
  return domain.split(".")[0] || domain;
}

/** Short label for an address: "chris" for chris@example.com, the domain when the local part is generic. */
export function accountLabel(email: string): string {
  const [local = email, domain = ""] = email.split("@");
  if (GENERIC_LOCAL_PARTS.has(local.toLowerCase()) && domain) return domainWord(domain);
  return local;
}

/**
 * Labels that tell a set of accounts apart: "chris" alone, but "work" / "home" when both
 * chris@work.example and chris@home.example are connected; full addresses as a last resort.
 */
export function accountLabels(emails: readonly string[]): Map<string, string> {
  const count = (labels: string[]) =>
    labels.reduce((m, l) => m.set(l, (m.get(l) ?? 0) + 1), new Map<string, number>());
  const short = emails.map(accountLabel);
  const shortCounts = count(short);
  const byDomain = emails.map((email, i) =>
    (shortCounts.get(short[i]!) ?? 0) > 1 ? domainWord(email.split("@")[1] ?? email) : short[i]!,
  );
  const domainCounts = count(byDomain);
  return new Map(
    emails.map((email, i) => [
      email,
      (domainCounts.get(byDomain[i]!) ?? 0) > 1 ? email : byDomain[i]!,
    ]),
  );
}

interface AccountChipProps {
  email: string;
  /** Disambiguated label (see accountLabels); defaults to the short label. */
  label?: string | undefined;
  colorIndex: number;
  className?: string;
  /** Show the full address instead of the short label. */
  full?: boolean;
}

export function AccountChip({
  email,
  label,
  colorIndex,
  className,
  full = false,
}: AccountChipProps) {
  return (
    <span
      title={email}
      className={cn(
        "inline-flex max-w-40 shrink-0 items-center gap-1.5 truncate rounded-full border border-line",
        "px-2 py-px text-[11px] leading-4 text-muted",
        className,
      )}
    >
      <span
        aria-hidden
        className="size-1.5 shrink-0 rounded-full"
        style={{ background: accountColor(colorIndex) }}
      />
      <span className="truncate">{full ? email : (label ?? accountLabel(email))}</span>
    </span>
  );
}
